// Text catalog for the "MI Support som arbejdsplads" (MI Support as a workplace)
// career page. The MI Support logo mark is rendered inline in two headings, so
// those headings are split into the parts around the logo.

import { type Locale } from "../config";
import { type PageMeta } from "../head";

type ArbejdspladsStrings = {
  meta: PageMeta;
  hero: { eyebrow: string; titleSuffix: string; body: string };
  team: { headingPrefix: string; body: string };
  stats: { value: string; label: string }[];
  staffIntro: string;
  quote1: { text: string; attrib: string };
  pillars: { heading: string; body: string }[];
  quote2: { text: string; attrib: string };
  wellbeing: { heading: string; body: string; items: string[] };
  cta: { heading: string; body: string; button: string };
};

export const arbejdspladsStrings: Record<Locale, ArbejdspladsStrings> = {
  da: {
    meta: {
      title: "MI Support som arbejdsplads - Lær os bedre at kende her",
      description:
        "Som virksomhed er MI Support baseret på vores handlekraftige, ansvarlige og motiverede medarbejdere. Lær os bedre at kende her.",
      ogImage: "https://www.misupport.dk/wp-content/uploads/MISupportIT_NTK4135-1-scaled.jpg",
    },
    hero: {
      eyebrow: "Karriere",
      titleSuffix: "som arbejdsplads",
      body: "Som virksomhed er MI Support baseret på vores handlekraftige, ansvarlige og motiverede medarbejdere, der med faglig stolthed arbejder målrettet og engageret for at løse vores kunders udfordringer. Lær os bedre at kende her.",
    },
    team: {
      headingPrefix: "Medarbejderne bag",
      body: "MI Support består i dag af +45 erfarne konsulenter i alle aldersgrupper og med mange forskellige personlige, faglige og uddannelsesmæssige baggrunde. Med diversitet som en af vores kerneværdier, værdsætter vi i høj grad forskellighederne blandt vores medarbejdere, og vi stræber efter at fremme en inkluderende kultur, hvor der er plads til alle.",
    },
    stats: [
      { value: "+45", label: "Antal medarbejdere i virksomheden" },
      { value: "23 år", label: "Alder på yngste medarbejder" },
      { value: "64 år", label: "Alder på ældste medarbejder" },
    ],
    staffIntro:
      "Vores team omfatter især ingeniører, bygningskonstruktører, maskinmestre samt medarbejdere med erhvervsfaglige uddannelser, der alle bringer mange års erfaring fra store byggeprojekter med sig.",
    quote1: {
      text: "Vi stræber efter at have kvalificerede medarbejdere med et højt fagligt niveau. Derfor prioriterer vi kontinuerlig kompetenceudvikling og tilbyder videreuddannelse for at sikre, at vores medarbejdere altid er på forkant med de nyeste metoder og teknologier inden for branchen.",
      attrib: "Sonny Christensen, Ejer & stifter af MI Support",
    },
    pillars: [
      {
        heading: "Lokal tilstedeværelse og central støtte",
        body: "Vores medarbejdere er geografisk spredt udover mange forskellige adresser, hovedsageligt på Sjælland, hvor de indgår som en del af vores kunders projektorganisationer. Dertil fungerer MI Supports hovedkontor i Greve som et centralt knudepunkt, hvor vores administration støtter op om projekterne og kan assistere inden for en lang række fagområder, så alle kan udføre deres arbejde bedst muligt.",
      },
      {
        heading: "Projektindsigt og faglig sparring",
        body: "Selvom MI Support er en ejerledet virksomhed, har vi en flad organisationsstruktur, hvor disse ledende medarbejdere selv har daglig gang ude på projekterne. Udover korte kommunikationsveje og hurtige beslutningsprocesser betyder det, at lederkredsen har dyb indsigt i de udfordringer, der kan opstå undervejs i byggeprocesserne, og kan byde ind med solid faglig sparring.",
      },
    ],
    quote2: {
      text: "At være en del af MI Support handler ikke kun om at levere resultater. Det handler også om at være en del af et fællesskab, hvor udvikling og samarbejde går hånd i hånd.",
      attrib: "Kristine Liljeberg, Head of Operations i MI Support",
    },
    wellbeing: {
      heading: "Fokus på trivsel og sammenhold",
      body: "I MI Support tror vi på, at stærke og langvarige relationer, både med kunder og internt i organisationen, er afgørende for en bæredygtig forretning. Derfor lægger vi stor vægt på at skabe et positivt og udviklende arbejdsmiljø, hvor vores medarbejdere føler sig som en vigtig del af vores sociale og faglige fællesskab. Vi arbejder målrettet på at fremme trivsel, faglig sparring og sammenhold gennem initiativer som:",
      items: [
        "Hyppige faglige events og workshops",
        "Sociale aktiviteter som fredagsbarer, fester og teambuilding",
        "Fokus på individuel og kollektiv kompetenceudvikling",
      ],
    },
    cta: {
      heading: "Er du vores nye kollega?",
      body: "I MI Support er vi altid på udkig efter nye, dygtige kollegaer, der har lyst til at blive en del af vores team. Vi har jævnligt ledige stillinger slået op, og ellers er du altid velkommen til at skrive dig op i vores CV-bank, som vi løbende screener for interessante profiler.",
      button: "Se vores jobmuligheder",
    },
  },

  en: {
    meta: {
      title: "MI Support as a workplace - Get to know us better here",
      description:
        "As a company, MI Support is built on our proactive, responsible and motivated employees. Get to know us better here.",
      ogImage: "https://www.misupport.dk/wp-content/uploads/MISupportIT_NTK4135-1-scaled.jpg",
    },
    hero: {
      eyebrow: "Career",
      titleSuffix: "as a workplace",
      body: "As a company, MI Support is built on our proactive, responsible and motivated employees, who work in a focused and dedicated way with professional pride to solve our customers' challenges. Get to know us better here.",
    },
    team: {
      headingPrefix: "The people behind",
      body: "MI Support today consists of more than 45 experienced consultants of all age groups and with many different personal, professional and educational backgrounds. With diversity as one of our core values, we greatly appreciate the differences among our employees, and we strive to promote an inclusive culture where there is room for everyone.",
    },
    stats: [
      { value: "+45", label: "Number of employees in the company" },
      { value: "23 years", label: "Age of the youngest employee" },
      { value: "64 years", label: "Age of the oldest employee" },
    ],
    staffIntro:
      "Our team comprises in particular engineers, architectural technologists, marine engineers and employees with vocational educations, who all bring many years of experience from large construction projects with them.",
    quote1: {
      text: "We strive to have qualified employees with a high professional level. That is why we prioritise continuous competence development and offer further education to ensure that our employees are always at the forefront of the latest methods and technologies in the industry.",
      attrib: "Sonny Christensen, Owner & founder of MI Support",
    },
    pillars: [
      {
        heading: "Local presence and central support",
        body: "Our employees are geographically spread across many different addresses, mainly on Zealand, where they form part of our customers' project organisations. In addition, MI Support's head office in Greve acts as a central hub, where our administration supports the projects and can assist within a wide range of professional fields, so that everyone can carry out their work in the best possible way.",
      },
      {
        heading: "Project insight and professional sparring",
        body: "Although MI Support is an owner-managed company, we have a flat organisational structure where these managers are themselves out on the projects on a daily basis. In addition to short communication paths and quick decision-making processes, this means that the management circle has deep insight into the challenges that can arise during the construction processes, and can contribute solid professional sparring.",
      },
    ],
    quote2: {
      text: "Being part of MI Support is not only about delivering results. It is also about being part of a community where development and collaboration go hand in hand.",
      attrib: "Kristine Liljeberg, Head of Operations at MI Support",
    },
    wellbeing: {
      heading: "Focus on well-being and togetherness",
      body: "At MI Support we believe that strong and long-lasting relationships, both with customers and internally within the organisation, are crucial for a sustainable business. That is why we place great emphasis on creating a positive and developing working environment, where our employees feel like an important part of our social and professional community. We work in a focused way to promote well-being, professional sparring and togetherness through initiatives such as:",
      items: [
        "Frequent professional events and workshops",
        "Social activities such as Friday bars, parties and team building",
        "Focus on individual and collective competence development",
      ],
    },
    cta: {
      heading: "Are you our new colleague?",
      body: "At MI Support we are always on the lookout for new, skilled colleagues who would like to become part of our team. We regularly post vacancies, and otherwise you are always welcome to sign up to our CV bank, which we continuously screen for interesting profiles.",
      button: "See our job opportunities",
    },
  },

  de: {
    meta: {
      title: "MI Support als Arbeitsplatz - Lernen Sie uns kennen",
      description:
        "Als Unternehmen baut MI Support auf unsere tatkräftigen, verantwortungsbewussten und motivierten Mitarbeiter. Lernen Sie uns hier besser kennen.",
      ogImage: "https://www.misupport.dk/wp-content/uploads/MISupportIT_NTK4135-1-scaled.jpg",
    },
    hero: {
      eyebrow: "Karriere",
      titleSuffix: "als Arbeitsplatz",
      body: "Als Unternehmen baut MI Support auf unsere tatkräftigen, verantwortungsbewussten und motivierten Mitarbeiter, die mit fachlichem Stolz zielgerichtet und engagiert daran arbeiten, die Herausforderungen unserer Kunden zu lösen. Lernen Sie uns hier besser kennen.",
    },
    team: {
      headingPrefix: "Die Mitarbeiter hinter",
      body: "MI Support besteht heute aus mehr als 45 erfahrenen Beratern aller Altersgruppen und mit vielen verschiedenen persönlichen, fachlichen und bildungsbezogenen Hintergründen. Mit Diversität als einem unserer Kernwerte schätzen wir die Unterschiede unter unseren Mitarbeitern in hohem Maße und streben danach, eine inklusive Kultur zu fördern, in der für alle Platz ist.",
    },
    stats: [
      { value: "+45", label: "Anzahl der Mitarbeiter im Unternehmen" },
      { value: "23 Jahre", label: "Alter des jüngsten Mitarbeiters" },
      { value: "64 Jahre", label: "Alter des ältesten Mitarbeiters" },
    ],
    staffIntro:
      "Unser Team umfasst insbesondere Ingenieure, Bautechniker, Maschinenmeister sowie Mitarbeiter mit berufsfachlichen Ausbildungen, die alle viele Jahre Erfahrung aus großen Bauprojekten mitbringen.",
    quote1: {
      text: "Wir streben danach, qualifizierte Mitarbeiter mit einem hohen fachlichen Niveau zu haben. Deshalb priorisieren wir die kontinuierliche Kompetenzentwicklung und bieten Weiterbildung an, um sicherzustellen, dass unsere Mitarbeiter stets an der Spitze der neuesten Methoden und Technologien der Branche stehen.",
      attrib: "Sonny Christensen, Inhaber & Gründer von MI Support",
    },
    pillars: [
      {
        heading: "Lokale Präsenz und zentrale Unterstützung",
        body: "Unsere Mitarbeiter sind geografisch über viele verschiedene Standorte verteilt, hauptsächlich auf Seeland, wo sie Teil der Projektorganisationen unserer Kunden sind. Hinzu kommt, dass die Hauptniederlassung von MI Support in Greve als zentraler Knotenpunkt fungiert, an dem unsere Verwaltung die Projekte unterstützt und in einer Vielzahl von Fachbereichen assistieren kann, sodass alle ihre Arbeit bestmöglich ausführen können.",
      },
      {
        heading: "Projekteinblick und fachliches Sparring",
        body: "Obwohl MI Support ein inhabergeführtes Unternehmen ist, haben wir eine flache Organisationsstruktur, in der diese leitenden Mitarbeiter selbst täglich auf den Projekten unterwegs sind. Neben kurzen Kommunikationswegen und schnellen Entscheidungsprozessen bedeutet das, dass der Führungskreis tiefe Einblicke in die Herausforderungen hat, die im Laufe der Bauprozesse entstehen können, und solides fachliches Sparring einbringen kann.",
      },
    ],
    quote2: {
      text: "Ein Teil von MI Support zu sein, bedeutet nicht nur, Ergebnisse zu liefern. Es bedeutet auch, Teil einer Gemeinschaft zu sein, in der Entwicklung und Zusammenarbeit Hand in Hand gehen.",
      attrib: "Kristine Liljeberg, Head of Operations bei MI Support",
    },
    wellbeing: {
      heading: "Fokus auf Wohlbefinden und Zusammenhalt",
      body: "Bei MI Support glauben wir, dass starke und langjährige Beziehungen, sowohl mit Kunden als auch intern in der Organisation, entscheidend für ein nachhaltiges Geschäft sind. Deshalb legen wir großen Wert darauf, ein positives und förderndes Arbeitsumfeld zu schaffen, in dem sich unsere Mitarbeiter als wichtiger Teil unserer sozialen und fachlichen Gemeinschaft fühlen. Wir arbeiten zielgerichtet daran, Wohlbefinden, fachliches Sparring und Zusammenhalt durch Initiativen zu fördern wie:",
      items: [
        "Häufige fachliche Events und Workshops",
        "Soziale Aktivitäten wie Freitagsbars, Feste und Teambuilding",
        "Fokus auf individuelle und kollektive Kompetenzentwicklung",
      ],
    },
    cta: {
      heading: "Sind Sie unser neuer Kollege?",
      body: "Bei MI Support sind wir stets auf der Suche nach neuen, fähigen Kollegen, die Lust haben, Teil unseres Teams zu werden. Wir schreiben regelmäßig freie Stellen aus, und ansonsten sind Sie jederzeit herzlich willkommen, sich in unsere CV-Bank einzutragen, die wir laufend nach interessanten Profilen durchsehen.",
      button: "Sehen Sie unsere Jobmöglichkeiten",
    },
  },
};
